//#region IMPORTS
import { InstoreOrderLineTableRow } from "../../models/instoreOrder";
import { useDrag, useDrop } from "react-dnd";
import { flexRender, Row } from "@tanstack/react-table";
import { VirtualItem } from "@tanstack/react-virtual";
import { useRef } from "react";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  virtualRow: VirtualItem;
  moveRow?: (toIndex: number, row: InstoreOrderLineTableRow) => void;
  row: Row<InstoreOrderLineTableRow>;
}
//#endregion

export default function InstoreOrderDetailTableBodyRow({
  virtualRow,
  moveRow,
  row,
}: Props) {
  //#region DND
  const ref = useRef<HTMLTableRowElement>(null);

  const [, drop] = useDrop({
    accept: "ROW",
    drop(item: { flatIndex: number; draggedRow: InstoreOrderLineTableRow }) {
      const from = item.flatIndex;
      const to = row.original.flatIndex!;
      if (moveRow && from !== to) {
        moveRow(to, item.draggedRow);
      }
    },
    canDrop: () => Boolean(moveRow),
  });

  const [{ isDragging }, drag] = useDrag({
    type: "ROW",
    canDrag: () => Boolean(moveRow) && row.getIsExpanded(),
    item: () => ({
      flatIndex: row.original.flatIndex,
      draggedRow: row.original,
    }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  if (moveRow) {
    drag(drop(ref));
  }
  //#endregion

  //#region RENDER
  return (
    <tr
      ref={ref}
      data-index={virtualRow.index}
      key={row.id}
      className={classNames(
        row.original.parent ? "bg-gray-100" : "",
        row.original.placeholder ? "border-amber-600" : "border-slate-300",
        "border-t h-12 w-full flex items-center absolute"
      )}
      style={{
        transform: `translateY(${virtualRow.start}px)`,
        height: "40px",
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {row.getVisibleCells().map((cell, i) => {
        const colSpan = row.original.parent && i === 1 ? 2 : 1;

        return (
          <td
            key={cell.id}
            colSpan={colSpan}
            className={classNames(
              row.original.parent
                ? "font-bold text-gray-800 text-sm"
                : row.original.placeholder
                ? "text-xs text-amber-800 animate-pulse"
                : "text-xs text-gray-600",
              "whitespace-nowrap px-2 text-left sm:pl-0"
            )}
            style={{ width: cell.column.getSize() }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
  //#endregion
}
